import { Recipe, RecipeInsert } from '../api/Recipe';
import { SupabaseService, supabase } from './SupabaseClient';
import { storageService } from './StorageService';
import { notificationService } from './NotificationService';
import { serviceMappers } from './serviceMappers';
import { RECIPE_WITH_PUBLIC_AUTHOR_SELECT } from './serviceSelectors';
import { applyRecipeSort } from './recipeSort';
import { deleteRecipeRecord, cleanupRecipeImage, fetchRecipeImageForDeletion } from './recipeImageCleanup';
import { notifyRecipeCreatedSafely } from './recipeNotifications';
import { findRecipeIdsByTags, insertRecipeTags, replaceRecipeTags } from './recipeTagPersistence';

export class RecipeService extends SupabaseService {
    private getDailySelectionDate(referenceDate: Date = new Date()): string {
        return referenceDate.toISOString().slice(0, 10);
    }

    private normalizeTagIds(tagIds: number[] | null | undefined): number[] {
        return tagIds ?? [];
    }

    private normalizeRecipe(recipe: Recipe): Recipe {
        return serviceMappers.normalizePublicRecipe(recipe);
    }

    private normalizeRecipes(recipes: Recipe[]): Recipe[] {
        return serviceMappers.normalizePublicRecipes(recipes);
    }

    public async getDailySelectionRecipe(
        selectionDate: string = this.getDailySelectionDate()
    ): Promise<Recipe | null> {
        const { data, error } = await supabase
            .from('daily_recipe_selections')
            .select(`recipe:recipes!daily_recipe_selections_recipe_id_fkey(${RECIPE_WITH_PUBLIC_AUTHOR_SELECT})`)
            .eq('selection_date', selectionDate)
            .maybeSingle();

        if (error) {
            throw new Error(error.message);
        }

        const recipe = (data as { recipe?: Recipe | null } | null)?.recipe || null;
        return recipe ? this.normalizeRecipe(recipe) : null;
    }

    public async getAllRecipes(
        offset: number = 0,
        limit: number = 10,
        selectedTagIds: number[] = [],
        selectedAuthorIds: number[] = [],
        sortKey: string = 'created_at_desc' // NEW: Add sortKey parameter
    ): Promise<Recipe[]> {
        let query = supabase
            .from('recipes')
            .select(RECIPE_WITH_PUBLIC_AUTHOR_SELECT);

        query = applyRecipeSort(query, sortKey);

        if (selectedTagIds.length > 0) {
            const filteredRecipeIds = await findRecipeIdsByTags(supabase, selectedTagIds);

            if (filteredRecipeIds.length === 0) {
                return [];
            }

            query = query.in('id', filteredRecipeIds);
        }

        if (selectedAuthorIds.length > 0) {
            query = query.in('author_id', selectedAuthorIds);
        }

        const { data: recipesData, error: recipesError } = await query.range(offset, offset + limit - 1);

        if (recipesError) throw new Error(recipesError.message);

        return this.normalizeRecipes(((recipesData || []) as unknown) as Recipe[]);
    }

    public async getRecipeById(id: string): Promise<Recipe> {
        const { data, error } = await supabase
            .from('recipes')
            .select(RECIPE_WITH_PUBLIC_AUTHOR_SELECT)
            .eq('id', id)
            .single();
        if (error) throw new Error(error.message);
        if (!data) throw new Error(`Recipe with id ${id} not found`);

        return this.normalizeRecipe((data as unknown) as Recipe);
    }

    public async addRecipe(recipeData: RecipeInsert, tagIds: number[]): Promise<Recipe> {
        const supabaseClient = await this.getClient();
        const normalizedTagIds = this.normalizeTagIds(tagIds);
        const { data: newRecipe, error: recipeError } = await supabaseClient.from('recipes').insert([recipeData]).select().single();
        if (recipeError) throw new Error(recipeError.message);

        await insertRecipeTags(supabaseClient, newRecipe.id, normalizedTagIds);

        const createdRecipe = await this.getRecipeById(newRecipe.id);
        notifyRecipeCreatedSafely(notificationService, createdRecipe.id, createdRecipe.title);
        return createdRecipe;
    }

    public async updateRecipe(recipe: Recipe, tagIds: number[]): Promise<Recipe> {
        const supabaseClient = await this.getClient();
        const normalizedTagIds = this.normalizeTagIds(tagIds);
        const { authors, tags, ...recipeToUpdate } = recipe;
        const { data: updatedRecipe, error: recipeError } = await supabaseClient.from('recipes').update(recipeToUpdate).eq('id', recipe.id).select().single();
        if (recipeError) throw new Error(recipeError.message);

        await replaceRecipeTags(supabaseClient, recipe.id, normalizedTagIds);

        return this.getRecipeById(updatedRecipe.id);
    }

    public async updateRecipeImage(recipeId: string, imageUrl: string | null): Promise<Recipe> {
        const supabaseClient = await this.getClient();
        const { data: updatedRecipe, error: recipeError } = await supabaseClient
            .from('recipes')
            .update({ image: imageUrl })
            .eq('id', recipeId)
            .select()
            .single();
        if (recipeError) throw new Error(recipeError.message);
        return this.getRecipeById(updatedRecipe.id);
    }

    public async deleteRecipe(recipeId: string): Promise<void> {
        const supabaseClient = await this.getClient();
        const recipeToDelete = await fetchRecipeImageForDeletion(supabaseClient, recipeId);

        cleanupRecipeImage(recipeToDelete, storageService);
        await deleteRecipeRecord(supabaseClient, recipeId);
    }
}

export const recipeService = new RecipeService();
